import { useEffect, useMemo, useRef, useState } from 'react';
import 'flag-icons/css/flag-icons.min.css';
import { Dropdown, Input, Option, OptionGroup, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '../../i18n';
import { useLocaleStore } from '../../store/locale.store';
import {
  FAVORITE_COUNTRIES,
  detectCountry,
  getCountry,
  otherCountries,
  type Country,
  type CountryLocale,
} from '../../utils/countries';
import { isValidNationalPhone, normalizeNationalInput, normalizePhoneInput } from '../../utils/validators';

const useStyles = makeStyles({
  row: { display: 'grid', gridTemplateColumns: 'minmax(0, 132px) minmax(0, 1fr)', gap: '8px', width: '100%' },
  dropdown: { minWidth: 0, width: '100%' },
  // Banderas en SVG (flag-icons): los emojis de bandera no se dibujan en Windows y salen como letras ("MX").
  triggerValue: { display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  flag: { marginRight: '8px', borderRadius: '2px', flexShrink: 0, verticalAlign: 'middle' },
  // La lista no hereda los 132 px del control: ancho cómodo y altura acotada para que no se salga de la pantalla.
  listbox: { minWidth: '280px', maxHeight: '320px' },
  error: { color: tokens.colorStatusDangerForeground1 },
});

export interface PhoneInputProps {
  /** Número en E.164 (`+52` + dígitos) o '' si no hay número. */
  value: string;
  onChange: (e164: string) => void;
  disabled?: boolean;
  id?: string;
  /** Etiqueta accesible del campo del número (por defecto, "Teléfono"). */
  ariaLabel?: string;
  /** `name` no estándar para que el navegador no empareje datos guardados. */
  name?: string;
  autoComplete?: string;
  onBlur?: () => void;
  onEnter?: () => void;
  /** Error de campo obligatorio (p. ej. vacío al enviar): borde rojo + mensaje debajo. */
  error?: string;
}

function compose(country: Country, national: string): string {
  return national ? `+${country.dial}${national}` : '';
}

export function PhoneInput({
  value, onChange, disabled, id, ariaLabel, name = 'agrovid-phone-number', autoComplete = 'off', onBlur, onEnter, error,
}: PhoneInputProps) {
  const s = useStyles();
  const t = useT();
  const locale = useLocaleStore(st => st.locale) as CountryLocale;

  const initial = detectCountry(value);
  const [country, setCountry] = useState<Country>(initial?.country ?? getCountry('MX'));
  const [national, setNational] = useState(initial?.national ?? '');
  const [touched, setTouched] = useState(false);
  const lastEmitted = useRef(value);

  // Cambios externos del valor (reset del formulario, precarga): resincroniza.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    const parsed = detectCountry(value);
    if (parsed) { setCountry(parsed.country); setNational(parsed.national); }
    else if (!value) setNational('');
  }, [value]);

  const others = useMemo(() => otherCountries(locale), [locale]);

  const emit = (c: Country, n: string) => {
    const next = compose(c, n);
    lastEmitted.current = next;
    onChange(next);
  };

  const handleText = (raw: string) => {
    const trimmed = raw.trim();
    // Pegado de un número internacional completo: detecta el país y llena el selector.
    if (trimmed.startsWith('+')) {
      const parsed = detectCountry(normalizePhoneInput(trimmed));
      if (parsed) {
        setCountry(parsed.country);
        setNational(parsed.national);
        emit(parsed.country, parsed.national);
        return;
      }
    }
    const digits = normalizeNationalInput(raw);
    setNational(digits);
    emit(country, digits);
  };

  const handleCountry = (iso: string | undefined) => {
    if (!iso) return;
    const c = getCountry(iso);
    setCountry(c);
    emit(c, national);
  };

  const invalid = !!error || (touched && national.length > 0 && !isValidNationalPhone(country.dial, national));
  const tenDigits = country.dial === '52' || country.dial === '1';
  const errorId = `${id ?? name}-error`;
  const flag = (c: Country) => (
    <span className={`fi fi-${c.iso.toLowerCase()} ${s.flag}`} aria-hidden="true" data-testid={`flag-${c.iso}`} />
  );

  const renderOption = (c: Country) => (
    <Option key={c.iso} value={c.iso} text={`${c[locale]} (+${c.dial})`}>
      {flag(c)}
      {c[locale]} (+{c.dial})
    </Option>
  );

  return (
    <div style={{ width: '100%' }}>
      <div className={s.row}>
        <Dropdown
          className={s.dropdown}
          aria-label={t.phoneField.countryLabel}
          disabled={disabled}
          selectedOptions={[country.iso]}
          value={`+${country.dial}`}
          // Debajo del control, alineada a su borde izquierdo; si no cabe, se abre hacia arriba.
          positioning={{ position: 'below', align: 'start', autoSize: 'height', fallbackPositions: ['above'] }}
          listbox={{ className: s.listbox }}
          button={{ children: <span className={s.triggerValue}>{flag(country)}+{country.dial}</span> }}
          onOptionSelect={(_, d) => handleCountry(d.optionValue)}
        >
          <OptionGroup label={t.phoneField.favorites}>{FAVORITE_COUNTRIES.map(renderOption)}</OptionGroup>
          <OptionGroup label={t.phoneField.allCountries}>{others.map(renderOption)}</OptionGroup>
        </Dropdown>
        <Input
          id={id}
          name={name}
          value={national}
          onChange={(_, d) => handleText(d.value)}
          onBlur={() => { setTouched(true); onBlur?.(); }}
          onKeyDown={e => { if (e.key === 'Enter') onEnter?.(); }}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          placeholder={t.phoneField.placeholder}
          aria-label={ariaLabel ?? t.phoneField.phoneLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : undefined}
          maxLength={22}
          disabled={disabled}
          style={invalid ? { border: `1px solid ${tokens.colorStatusDangerBorder2}` } : undefined}
        />
      </div>
      {invalid && (
        <Text id={errorId} role="alert" size={200} className={s.error}>
          {error ?? (tenDigits ? t.phoneField.invalidTen : t.phoneField.invalidGeneric)}
        </Text>
      )}
    </div>
  );
}
